/**
 * CSRF Token Utility for SPA Security
 *
 * Implements double-submit cookie pattern for CSRF protection:
 * 1. Backend sets XSRF-TOKEN cookie (readable by JS)
 * 2. Frontend reads cookie and sends value as X-XSRF-TOKEN header
 * 3. Backend verifies header matches cookie
 *
 * This prevents CSRF attacks because:
 * - Attacker's site cannot read our cookies (same-origin policy)
 * - Attacker cannot set the header value to match a cookie they can't read
 *
 * @author Vlad Karapetyan
 * @copyright 2026 Lickly Inc.
 */

import { API_HOST } from '../constants/apiUrls';
import configData from '../config/loader';

// Cache the CSRF token to avoid repeated cookie parsing
let cachedToken = null;

/**
 * Gets the CSRF token from the XSRF-TOKEN cookie.
 *
 * @returns {string|null} The CSRF token or null if not found
 */
export function getCsrfToken() {
  if (cachedToken) {
    return cachedToken;
  }

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN') {
      // Decode URI component in case the token was encoded
      cachedToken = decodeURIComponent(value);
      return cachedToken;
    }
  }

  return null;
}

/**
 * Clears the cached CSRF token.
 * Call this on logout or when the token might have changed.
 */
export function clearCsrfToken() {
  cachedToken = null;
}

/**
 * Fetches the CSRF cookie from the server.
 * Call this on app initialization to ensure the cookie is set.
 *
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export async function initCsrfToken() {
  try {
    const response = await fetch(`${API_HOST}/csrf-cookie`, {
      method: 'GET',
      credentials: 'include', // Important: include cookies in the request
      headers: {
        'X-Tenant': configData.tenantName,
      },
    });

    if (response.ok) {
      // Clear cache so next getCsrfToken() reads the new cookie
      clearCsrfToken();
      return true;
    }

    console.warn('Failed to fetch CSRF cookie:', response.status);
    return false;
  } catch (error) {
    console.warn('Failed to fetch CSRF cookie:', error.message);
    return false;
  }
}

/**
 * Adds CSRF token to request headers.
 * Use this with fetch() or XMLHttpRequest.
 *
 * @param {Headers|Object} headers - Headers object or plain object
 * @returns {Headers|Object} Headers with CSRF token added
 */
export function addCsrfHeader(headers = {}) {
  const token = getCsrfToken();

  if (!token) {
    // Silent - token will be set by server on first request
    return headers;
  }

  if (headers instanceof Headers) {
    headers.set('X-XSRF-TOKEN', token);
  } else {
    headers['X-XSRF-TOKEN'] = token;
  }

  return headers;
}

/**
 * Sets CSRF header on an XMLHttpRequest.
 *
 * @param {XMLHttpRequest} xhr - The XMLHttpRequest object
 */
export function setCsrfHeaderOnXhr(xhr) {
  const token = getCsrfToken();

  if (token) {
    xhr.setRequestHeader('X-XSRF-TOKEN', token);
  }
  // Silent if no token - will be set by server on first request
}

export default {
  getCsrfToken,
  clearCsrfToken,
  initCsrfToken,
  addCsrfHeader,
  setCsrfHeaderOnXhr,
};
